import { sync } from 'vuex-pathify';

import { Draw, Modify, Snap } from 'ol/interaction';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { formatArea, formatLength } from '@/assets/js/mapUtils';
import { transform } from 'ol/proj.js';
import { circular } from 'ol/geom/Polygon.js';
import { getDistance } from 'ol/sphere.js';
import { KML, GeoJSON } from 'ol/format';
import { GeometryCollection } from 'ol/geom';
import Feature from 'ol/Feature';

export default {
  computed: {
    measurementGeometry: sync('map/measurementGeometry'),
    isMeasurementsToolActive: sync('tools/toolStatus@isMeasurementsToolActive'),
  },
  data: () => ({
    typeStyleFunctionsDist: {
      Polygon: 'getAreaMeasurementStyles',
      MultiPolygon: 'getAreaMeasurementStyles',
      LineString: 'getLengthMeasurementStyles',
      MultiLineString: 'getLengthMeasurementStyles',
      Circle: 'getCircleMeasurementStyles',
    },
  }),
  methods: {
    clearMeasurementInteraction() {
      if (!this.map) return;
      [
        this.getInteractionByName('measurementDrawInteraction'),
        this.getInteractionByName('measurementModifyInteraction'),
        this.getInteractionByName('measurementSnapInteraction'),
      ].every(interaction => interaction && this.map.removeInteraction(interaction));
      if (this.getLayerById('measurement')) this.map.removeLayer(this.getLayerById('measurement'));
      this.measurementGeometry = null;
      this.isMeasurementsToolActive = false;
      this.activeTool = undefined;
      this.isActiveToolNotReplaceable = false;
    },
    getPolygonMeasurementLabel(geometry) {
      return `${this.$i18n.t('map.area')}: ${formatArea(geometry)}
      ${this.$i18n.t('map.perimeter')}: ${formatLength(geometry)}`;
    },
    getMeasurementStyle(feature, type, disablePointStyle) {
      const geometry = feature.getGeometry();
      return [
        ...(disablePointStyle ? [] : this.getPointMeasurementStyle({ geometry })),
        ...this[this.typeStyleFunctionsDist[type]]({ geometry, feature }),
      ];
    },
    addMeasurementLayer(type) {
      const layer = new VectorLayer({
        source: new VectorSource({}),
        id: 'measurement',
        style: feature => this.getMeasurementStyle(feature, type),
        zIndex: 1000,
      });
      this.map.addLayer(layer);
      return layer;
    },
    getMeasurementModifyStyleHandler(feature, isCircle, type) {
      if (isCircle) {
        feature.get('features').forEach(modifyFeature => {
          const modifyGeometry = modifyFeature.get('modifyGeometry');
          if (modifyGeometry) {
            const modifyPoint = feature.getGeometry().getCoordinates();
            const geometries = modifyFeature.getGeometry().getGeometries();
            const polygon = geometries[0].getCoordinates()[0];
            const center = geometries[1].getCoordinates();
            const projection = this.map.getView().getProjection();
            let first, last, radius;
            if (modifyPoint[0] === center[0] && modifyPoint[1] === center[1]) {
              first = transform(polygon[0], projection, 'EPSG:4326');
              last = transform(polygon[(polygon.length - 1) / 2], projection, 'EPSG:4326');
              radius = getDistance(first, last) / 2;
            } else {
              first = transform(center, projection, 'EPSG:4326');
              last = transform(modifyPoint, projection, 'EPSG:4326');
              radius = getDistance(first, last);
            }
            const circle = circular(transform(center, projection, 'EPSG:4326'), radius, 128);
            circle.transform('EPSG:4326', projection);
            geometries[0].setCoordinates(circle.getCoordinates());
            modifyGeometry.setGeometries(geometries);
          }
        });
        return this.getMeasurementStyle(feature, type, true);
      } else {
        return this.getMeasurementStyle(feature, type, true);
      }
    },
    addMeasurement(type) {
      if (!this.map) return;
      this.measurementGeometry = null;
      const drawLayer = this.addMeasurementLayer(type);
      this.addMeasurementInteraction(drawLayer, type);
    },
    getMeasurementGeometryGeojson(feature) {
      const measurementFeaturesArray = feature
        ? [feature]
        : this.getLayerById('measurement')
            .getSource()
            .getFeatures();
      return JSON.stringify({
        type: 'FeatureCollection',
        crs: {
          properties: {
            name: this.$_config.defaultEpsg,
          },
          type: 'name',
        },
        features: [
          new GeoJSON().writeFeatureObject(
            measurementFeaturesArray[0].getGeometry() instanceof GeometryCollection
              ? new Feature({ geometry: measurementFeaturesArray[0].getGeometry().getGeometries()[0] })
              : measurementFeaturesArray[0],
            {
              featureProjection: this.$_config.defaultEpsg || 'EPSG:4326',
              dataProjection: this.$_config.defaultEpsg || 'EPSG:4326',
            }
          ),
        ],
      });
    },
    addMeasurementInteraction(layer, type) {
      this.deactivateToolHandler();
      const isCircle = type === 'Circle';
      const drawInteraction = new Draw({
        source: layer.getSource(),
        ...(isCircle && {
          geometryFunction: (coords, geom, proj) => this.createPolygonFromCircle(coords, geom, proj, true),
        }),
        type: type,
        style: feature => this.getMeasurementStyle(feature, type),
      });
      drawInteraction.set('name', 'measurementDrawInteraction');
      drawInteraction.on('drawstart', () => {
        this.getLayerById('measurement')
          .getSource()
          .clear();
      });

      drawInteraction.on('drawend', e => {
        this.measurementGeometry = JSON.parse(this.getMeasurementGeometryGeojson(e.feature));
      });
      const modifyInteraction = new Modify({
        source: layer.getSource(),
        style: feature => this.getMeasurementModifyStyleHandler(feature, isCircle, type),
      });
      modifyInteraction.set('name', 'measurementModifyInteraction');
      if (isCircle) {
        modifyInteraction.on('modifystart', event => {
          event.features.forEach(feature => {
            const geometry = feature.getGeometry();
            if (geometry.getType() === 'GeometryCollection') feature.set('modifyGeometry', geometry.clone(), true);
          });
        });

        modifyInteraction.on('modifyend', event => {
          event.features.forEach(feature => {
            const modifyGeometry = feature.get('modifyGeometry');
            if (modifyGeometry) {
              feature.setGeometry(modifyGeometry);
              feature.unset('modifyGeometry', true);
            }
          });
        });
      }

      const snapInteraction = new Snap({
        source: layer.getSource(),
      });
      snapInteraction.set('name', 'measurementSnapInteraction');

      this.map.addInteraction(drawInteraction);
      this.map.addInteraction(modifyInteraction);
      this.map.addInteraction(snapInteraction);
      this.isMeasurementsToolActive = true;
    },
    turnOffMeasurementsInteraction() {
      this.map.removeInteraction(this.getInteractionByName('measurement'));
    },
  },
  mounted() {
    /**
     * Methods with parameters has to be wrapped in order to not be executed
     * during proccess of attachment.
     */
    this.$root.$on('measurementArea-action', (value = true) => {
      if (value) {
        this.addMeasurement('Polygon');
        this.activeTool = 'measurementArea';
      } else {
        this.clearMeasurementInteraction();
      }
    });
    this.$root.$on('measurementLength-action', (value = true) => {
      if (value) {
        this.addMeasurement('LineString');
        this.activeTool = 'measurementLength';
      } else {
        this.clearMeasurementInteraction();
      }
    });
    this.$root.$on('measurementRadius-action', (value = true) => {
      if (value) {
        this.addMeasurement('Circle');
        this.activeTool = 'measurementRadius';
      } else {
        this.clearMeasurementInteraction();
      }
    });
    this.$root.$on('exportMeasurement', type => {
      const contentString =
        type === 'kml'
          ? `<?xml version="1.0" encoding="utf-8" ?>${new KML().writeFeatures(
              new GeoJSON().readFeatures(this.measurementGeometry),
              {
                featureProjection: this.$_config.defaultEpsg || 'EPSG:4326',
                dataProjection: 'EPSG:4326',
              }
            )}`
          : JSON.stringify(this.measurementGeometry);
      const file = new Blob([contentString], {
        type: `${type === 'kml' ? 'text/xml' : 'application/geo+json'};charset=utf-8;`,
      });
      this.$_saveFile(URL.createObjectURL(file), `${this.$i18n.t('sidebar.measurements')}.${type}`);
    });
  },
};
